import React, { useMemo, useRef } from 'react';
// Fix: Use named imports for ReactFlow components and types.
import ReactFlow, {
  Background,
  BackgroundVariant,
  MiniMap,
  Node,
  Edge,
} from 'reactflow';

import { AppNode, SimulationState, ProcessDefinition } from '../../types';
import TopBar from '../organisms/TopBar';
import SimulationOverlay from '../organisms/SimulationOverlay';
import BottomPanel from '../organisms/BottomPanel';
import CustomNode from '../molecules/CustomNode';
import { useEditorConfig } from '../../contexts/EditorContext';
import { CanvasSearch } from '../molecules/CanvasSearch';
import { useSearch } from '../../hooks/useSearch';
import { useTheme } from '../../contexts/ThemeContext';
import { CANVAS_STATUS_COLORS, CANVAS_BACKGROUND_COLORS } from '../../constants';

/**
 * Map a node's simulation status to its minimap stroke color.
 * Extracted to module scope so it is not re-created on each render.
 */
const miniMapNodeStrokeColor = (n: Node<AppNode['data']>): string => {
    const status = n.data?.status;
    if (status === 'error') return CANVAS_STATUS_COLORS.error;
    if (status === 'active') return CANVAS_STATUS_COLORS.active;
    if (status === 'completed') return CANVAS_STATUS_COLORS.completed;
    return CANVAS_STATUS_COLORS.default;
};

export interface WorkflowSimulatorProps {
    nodes: AppNode[];
    edges: Edge[];

    // Simulation
    simulationState: SimulationState;
    onToggleSimulation: () => void;
    onResetSimulation: () => void;
    onSimulationAction: (action: 'NEXT' | 'BACK' | 'STOP' | 'SELECT_START' | 'PROCESS_QUEUE', data?: Record<string, unknown>) => void;
    suggestedPath: string | null;

    // Process Mgmt (for TopBar read-only display)
    activeProcessId: string;
    processes: ProcessDefinition[];
    onSelectProcess: (id: string) => void; // Usually disabled in sim, but kept for interface consistency
    onCreateProcess: () => void;
    onDeleteProcess: (id: string) => void;
    onSaveProject: (opts?: { bypassValidation?: boolean }) => void;
    onLoadProject: (file: File) => void;

    // Host Callbacks
    onSave?: () => void;
    onSaveAndQuit?: () => void;
    onBack?: () => void;

    // Read-only interactions
    onNodeClick?: (e: React.MouseEvent, node: Node) => void;
}

const WorkflowSimulator: React.FC<WorkflowSimulatorProps> = (props) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const { nodeTypes: configNodeTypes } = useEditorConfig();
    const { theme } = useTheme();

    const nodeTypes = useMemo(() => {
        const types: Record<string, typeof CustomNode> = {};
        Object.keys(configNodeTypes).forEach(key => {
            types[key] = CustomNode;
        });
        return types;
    }, [configNodeTypes]);

    const { query, setQuery, matches, activeIndex, nextMatch } = useSearch({
      nodes: props.nodes,
      onSelectNode: (id) => {
        const node = props.nodes.find(n => n.id === id);
        if (node && props.onNodeClick) props.onNodeClick({} as React.MouseEvent, node);
      }
    });

    return (
        <div className={`flex flex-col h-full w-full overflow-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans ${theme}`}>
          <TopBar
            simulationState={props.simulationState}
            onToggleSimulation={props.onToggleSimulation}
            onReset={props.onResetSimulation}
            onExport={props.onSaveProject}
            onImport={props.onLoadProject}
            onSave={props.onSave}
            onSaveAndQuit={props.onSaveAndQuit}
            onBack={props.onBack}
            processes={props.processes}
            activeProcessId={props.activeProcessId}
            onSelectProcess={props.onSelectProcess}
            onCreateProcess={props.onCreateProcess}
            onDeleteProcess={props.onDeleteProcess}
            currentNodes={props.nodes}
            onAutoLayout={() => {}} // Disabled in simulator mode
          />

          <div className="flex flex-1 overflow-hidden relative">
                {/* Main Canvas Area + Bottom Panel */}
                <div className="flex-1 flex flex-col h-full relative min-w-0">
                    <div className="flex-1 relative bg-slate-100 dark:bg-slate-950/50" ref={reactFlowWrapper}>
                        <CanvasSearch
                          query={query}
                          onQueryChange={setQuery}
                          matchCount={matches.length}
                          activeIndex={activeIndex}
                          onNext={nextMatch}
                          onClear={() => setQuery('')}
                        />

                        <ReactFlow
                            nodes={props.nodes}
                            edges={props.edges}
                            nodeTypes={nodeTypes}
                            fitView
                            style={{ width: '100%', height: '100%' }}
                            proOptions={{ hideAttribution: true }}
                            snapToGrid={true}
                            snapGrid={[15, 15]}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable={false}
                            panOnDrag={true}
                            zoomOnScroll={true}
                            preventScrolling={true}
                            defaultEdgeOptions={{
                                interactionWidth: 20,
                                style: { strokeWidth: 2, stroke: CANVAS_STATUS_COLORS.default }
                            }}
                        >
                            <MiniMap
                                nodeStrokeColor={miniMapNodeStrokeColor}
                                zoomable
                                pannable
                                className="!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-800 !shadow-sm !rounded-xl"
                            />
                            <Background
                                color={theme === 'dark' ? CANVAS_BACKGROUND_COLORS.dark : CANVAS_BACKGROUND_COLORS.light}
                                gap={20}
                                size={1}
                                variant={BackgroundVariant.Dots}
                            />
                        </ReactFlow>
                    </div>

                    {/* Re-use Bottom Panel for Logs/Variables */}
                    <BottomPanel simulationState={props.simulationState} />
                </div>

                {/* Right Sidebar for Simulation Interaction */}
                <div className="w-[420px] bg-white border-l border-slate-200 dark:border-slate-800 dark:bg-slate-950 shadow-xl z-30 flex flex-col">
                    <SimulationOverlay
                        simulationState={props.simulationState}
                        nodes={props.nodes}
                        edges={props.edges}
                        onAction={props.onSimulationAction}
                        suggestedPath={props.suggestedPath}
                    />
                </div>
          </div>
        </div>
      );
};

export default WorkflowSimulator;
